import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Button,
    Card,
    CardContent,
    FormControlLabel,
    Snackbar,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import _ from 'lodash';

import { useUser } from '../../app/Contexts';
import { Icon } from '../../shared/Icon';
import { ProtectedButton } from '../../shared/ProtectedButton';
import { Spinner } from '../../shared/Spinner';
import { ToggleButton, ToggleButtonGroup } from '../../shared/ToggleButton';
import { CategoryModal } from '../Category/CategoryModal';
import {
    PortalPublication,
    portalPublicationToPutFeaturedPostsPublicationDTO,
    portalPublicationToPutPortalPublicationDTO,
    publicationDtoToPortalPublication,
} from '../PortalPublication';
import { CategoryListBox } from './CategoryListBox';
import { PortalPublicationFeedTable } from './PortalPublicationFeedTable';
import { PortalPublicationTimeline } from './PortalPublicationTimeline';
import { Modal } from '../../shared';
import { TemplateId, UserRoles } from '@lifeserver/shared';

import {
    createChangelogDetailsCreated,
    createChangelogDetailsUpdated,
    useSaveChangeLog,
} from '../../hooks/changeLog';
import {
    PortalCategoryDTO,
    PutFeaturedPostsDTO,
    useDuplicatePortalPost,
    usePortalApi,
    usePortalCategories,
    usePortalFeaturedPosts,
    usePortalSchedule,
    useSaveScheduleByCategory,
    useUpsertPortalCategory,
} from '../../hooks/portal';
import { useIsAuthorized } from '../../hooks/useIsAuthorized';

import { assert } from '../../utils/assert';
import { handleEnvAuth } from '../../utils/envaccess';
import { errorLikeToMessage } from '../../utils/error';

import { RouteTo } from '../../routes';

import {
    compactSchedule,
    copyPortalCategory,
    copyPortalPosts,
    promptAndDuplicatePost,
} from '../portalUtils';
import styles from './PortalPublicationFeed.module.scss';

const FEATURED_OVERLAP_LIMIT = 1;
const CATEGORY_OVERLAP_LIMIT = 10;
const DEFAULT_DUPLICATE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

const EVENTS_CALENDAR_ALLOWED_TEMPLATES: TemplateId[] = [
    'event-calendar-small',
    'event-calendar-medium',
];

export const PortalPublicationFeed = () => {
    const user = useUser();
    const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);
    const { data: categories } = usePortalCategories();
    const [selectedCategoryIdRaw, setSelectedCategory] = useState<string>();
    const selectedCategoryId =
        selectedCategoryIdRaw ?? categories?.[0]?.id ?? 'featured';

    const [isEditingCategories, setIsEditingCategories] = useState(false);

    const portalApi = usePortalApi();
    const saveScheduleByCategory = useSaveScheduleByCategory();
    const { data: schedule } = usePortalSchedule(
        selectedCategoryId === 'featured' ? undefined : selectedCategoryId,
    );
    const { data: featuredPosts, saveFeaturedPosts } = usePortalFeaturedPosts();

    const updateCategory = useUpsertPortalCategory();
    const selectedCategory = categories?.find(
        (c) => c.id === selectedCategoryId,
    );
    const selectedCategoryType = selectedCategory?.type;
    const isManual =
        selectedCategoryId === 'featured' ||
        selectedCategoryType === 'manual' ||
        selectedCategoryType === 'eventsCalendar';
    const saveChangeLog = useSaveChangeLog();

    const publications = useMemo<PortalPublication[] | undefined>(() => {
        if (selectedCategoryId === undefined) {
            return;
        } else if (selectedCategoryId === 'featured') {
            return featuredPosts?.featuredPosts?.map(
                publicationDtoToPortalPublication,
            );
        } else {
            return schedule?.publications?.map(
                publicationDtoToPortalPublication,
            );
        }
    }, [featuredPosts, schedule, selectedCategoryId]);

    const { isAuthorized: isCategoryWriter } = useIsAuthorized(
        UserRoles.PortalWriteCategory,
    );

    if (!categories || selectedCategoryId === undefined) {
        return <Spinner />;
    }

    const savePublications = async (
        publications: PortalPublication[],
        basePath?: string,
    ) => {
        if (selectedCategoryId === 'featured') {
            const putFeaturedPostsDto: PutFeaturedPostsDTO = {
                featuredPosts: publications.map(
                    portalPublicationToPutFeaturedPostsPublicationDTO,
                ),
            };

            const response = await saveFeaturedPosts.mutateAsync({
                putFeaturedPostsDto,
                basePath,
            });

            return response.featuredPosts.map(
                publicationDtoToPortalPublication,
            );
        } else {
            const putScheduleDto = {
                publications: publications.map(
                    portalPublicationToPutPortalPublicationDTO,
                ),
            };

            await saveScheduleByCategory.mutateAsync({
                categoryId: selectedCategoryId,
                updateScheduleByCategoryDto: putScheduleDto,
                basePath,
            });
            return publications;
        }
    };

    const copyToEnvironment = async () => {
        const targetEnv = await handleEnvAuth();
        if (!targetEnv || !publications) {
            return;
        }

        const posts = publications.map((publication) => publication.post);

        try {
            if (selectedCategoryId !== 'featured') {
                const category = categories?.find(
                    (category) => category.id === selectedCategoryId,
                );
                assert(category, "Couldn't find selected category");
                await copyPortalCategory(portalApi, category, targetEnv);
            }
            await copyPortalPosts(
                portalApi,
                selectedCategoryId,
                posts,
                targetEnv,
            );
            await savePublications(publications, targetEnv.basepath);
            await Modal.alert({
                heading: 'Copying done!',
                text: 'Pulication feed has been copied successfully.',
            });
        } catch (e) {
            await Modal.alert({
                heading: 'On no!',
                text: `Something went wrong when trying to copy the publication feed.\n\n ${errorLikeToMessage(
                    e,
                )}`,
            });
        }
    };
    const handleOpenCategoryModal = async () => {
        setCategoryModalOpen(true);
    };

    const handleCloseCategoryModal = () => {
        setCategoryModalOpen(false);
    };
    const handleSaveCategory = async (category: PortalCategoryDTO) => {
        if (!user) {
            throw new Error('No user logged in!');
        }
        const oldCategory = categories?.find((c) => c.id === category.id);
        const isUpdate = !!oldCategory;
        await updateCategory.mutateAsync({
            categoryId: category.id,
            upsertPortalCategoryDTO: category,
        });
        await saveChangeLog.mutateAsync({
            createChangeLogEntryDto: {
                userId: user.email,
                action: isUpdate ? 'updated' : 'created',
                entity: 'category',
                entityId: category.id,
                details: isUpdate
                    ? JSON.stringify(
                          createChangelogDetailsUpdated(oldCategory, category),
                      )
                    : JSON.stringify(createChangelogDetailsCreated(category)),
            },
        });
        setCategoryModalOpen(false);
    };

    return (
        <div>
            <CategoryModal
                open={isCategoryModalOpen}
                onClose={handleCloseCategoryModal}
                onSave={handleSaveCategory}
            />
            <div className={styles.categoryHeader}>
                <Card sx={{ display: isCategoryWriter ? 'undefined' : 'none' }}>
                    <CardContent>
                        <Typography variant="h5">Category Controls</Typography>
                        <Stack>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isEditingCategories}
                                        onChange={(_, checked) => {
                                            setIsEditingCategories(checked);
                                        }}
                                    />
                                }
                                label="Edit Categories"
                            />
                            <Button
                                variant="contained"
                                onClick={handleOpenCategoryModal}
                                startIcon={<Icon name="add" />}
                                className={styles.createButton}
                            >
                                Add Category
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
                <CategoryListBox
                    categories={categories}
                    selectedCategory={selectedCategoryId}
                    onCategorySaved={handleSaveCategory}
                    onCategorySelected={setSelectedCategory}
                    editable={isEditingCategories}
                />
            </div>
            {publications && (
                <PortalPublicationFeedInternal
                    key={selectedCategoryId}
                    publications={publications}
                    savePublications={savePublications}
                    copyToEnvironment={copyToEnvironment}
                    isSaving={saveScheduleByCategory.isPending}
                    maxLanes={
                        selectedCategoryId === 'featured'
                            ? FEATURED_OVERLAP_LIMIT
                            : CATEGORY_OVERLAP_LIMIT
                    }
                    locked={!isManual}
                    allowedTemplates={
                        selectedCategoryType === 'eventsCalendar'
                            ? EVENTS_CALENDAR_ALLOWED_TEMPLATES
                            : undefined
                    }
                />
            )}
        </div>
    );
};

const PortalPublicationFeedInternal = (props: {
    publications: PortalPublication[];
    savePublications?: (
        publications: PortalPublication[],
    ) => Promise<PortalPublication[]>;
    copyToEnvironment?: () => Promise<void>;
    isSaving?: boolean;
    maxLanes: number;
    locked?: boolean;
    allowedTemplates?: TemplateId[];
}) => {
    const navigate = useNavigate();
    const duplicatePortalPostMutation = useDuplicatePortalPost();
    const [editingPublications, setPublications] = useState(
        props.publications.some(
            (publication) => publication.position >= props.maxLanes,
        )
            ? compactSchedule(props.publications)
            : props.publications,
    );
    // If we're locked, ensure we stay in sync with changes from the initial state
    const publications = props.locked
        ? props.publications
        : editingPublications;

    const [savedPublications, setSavedPublications] = useState(publications);
    const [mode, setMode] = useState<string>('timeline');
    const [isCopying, setIsCopying] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const onPublicationAdded = (publication: PortalPublication) => {
        setPublications((publications) => {
            return [...publications, publication];
        });
    };

    const onPublicationRemoved = (id: string) => {
        if (props.locked) {
            setSnackbarMessage(
                'This category is managed automatically and cannot be edited.',
            );
            setSnackbarOpen(true);
            return;
        }
        setPublications((publications) => {
            return publications.filter((publication) => publication.id !== id);
        });
    };

    const onPublicationDuplicated = async (id: string) => {
        if (props.locked) {
            setSnackbarMessage(
                'This category is managed automatically and cannot be edited.',
            );
            setSnackbarOpen(true);
            return;
        }
        const source = publications.find((p) => p.id === id);
        if (!source) return;

        await promptAndDuplicatePost(
            source.post.id,
            (newName) =>
                duplicatePortalPostMutation.mutateAsync({
                    postId: source.post.id,
                    newName,
                }),
            (newPost) => {
                // Place immediately after the original, preserving its duration.
                const duration = source.endTime
                    ? source.endTime.getTime() - source.startTime.getTime()
                    : DEFAULT_DUPLICATE_DURATION_MS;
                const newStart = source.endTime ?? new Date();
                const newEnd = new Date(newStart.getTime() + duration);

                onPublicationAdded({
                    id: crypto.randomUUID(),
                    post: newPost,
                    startTime: newStart,
                    endTime: newEnd,
                    position: source.position,
                });
            },
        );
    };

    const onPublicationEdit = (id: string) => {
        const publication = publications.find((p) => p.id === id);
        if (!publication) return;
        navigate(RouteTo.PortalPostEdit(publication.post.id));
    };

    const hasChanges = useMemo(
        () => !_.isEqual(publications, savedPublications),
        [publications, savedPublications],
    );

    const saveChanges = () => {
        if (!hasChanges) {
            return;
        }

        props.savePublications?.(publications).then((savedPublications) => {
            setPublications(savedPublications);
            setSavedPublications(savedPublications);
        });
    };

    const setStartTime = (publicationId: string, startTime: Date) => {
        setPublications((publications) =>
            publications.map((publication) =>
                publication.id === publicationId
                    ? {
                          ...publication,
                          startTime,
                      }
                    : publication,
            ),
        );
    };

    const setEndTime = (publicationId: string, endTime: Date) => {
        setPublications((publications) =>
            publications.map((publication) =>
                publication.id === publicationId
                    ? {
                          ...publication,
                          endTime,
                      }
                    : publication,
            ),
        );
    };

    const onPublicationChanged = (
        id: PortalPublication['id'],
        changedPublication: Partial<PortalPublication>,
    ) => {
        setPublications((publications) =>
            publications.map((publication) =>
                publication.id === id
                    ? {
                          ...publication,
                          ..._.omitBy(
                              changedPublication,
                              (value) => value === undefined,
                          ),
                      }
                    : publication,
            ),
        );
    };

    const copyToEnvironment = async () => {
        assert(
            !hasChanges,
            'Button should be disabled if there are any changes',
        );

        setIsCopying(true);

        try {
            await props.copyToEnvironment?.();
        } finally {
            setIsCopying(false);
        }
    };

    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneDisplayName =
        localTimezone.split('/').pop()?.replace(/_/g, ' ') ?? localTimezone;
    const utcOffsetHours = -new Date().getTimezoneOffset() / 60;
    const utcOffsetLabel = `UTC ${utcOffsetHours >= 0 ? '+' : ''}${utcOffsetHours}`;

    return (
        <>
            <div className={styles.toggleModeContainer}>
                <ToggleButtonGroup
                    value={mode}
                    onToggle={(value) => setMode(value)}
                >
                    <ToggleButton value="timeline">Timeline</ToggleButton>
                    <ToggleButton value="table">Table</ToggleButton>
                </ToggleButtonGroup>
            </div>

            <div className={styles.timezoneNotice}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className={styles.timezoneNoticeIcon}
                    width="1em"
                    height="1em"
                    aria-hidden="true"
                    focusable="false"
                >
                    <path
                        fill="currentColor"
                        d="M4.47 21h15.06c1.54 0 2.5-1.67 1.73-3L13.73 4.99c-.77-1.33-2.69-1.33-3.46 0L2.74 18c-.77 1.33.19 3 1.73 3M12 14c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1s1 .45 1 1v2c0 .55-.45 1-1 1m1 4h-2v-2h2z"
                    />
                </svg>
                <div>
                    <span className={styles.timezoneNoticeTitle}>
                        The CMS always shows time in the browser's local time
                        zone!
                    </span>
                    <Typography
                        variant="body2"
                        className={styles.timezoneNoticeSubtitle}
                    >
                        Current local timezone: {timezoneDisplayName} (
                        {utcOffsetLabel})
                    </Typography>
                </div>
            </div>

            {mode === 'timeline' ? (
                <PortalPublicationTimeline
                    lanes={props.maxLanes}
                    publications={publications}
                    onPublicationAdded={onPublicationAdded}
                    onPublicationRemove={onPublicationRemoved}
                    onPublicationDuplicate={onPublicationDuplicated}
                    onPublicationEdit={onPublicationEdit}
                    onPublicationChanged={onPublicationChanged}
                    locked={props.locked}
                    allowedTemplates={props.allowedTemplates}
                />
            ) : (
                <>
                    <PortalPublicationFeedTable
                        originalPublications={props.publications}
                        publications={publications}
                        onStartTimeChange={setStartTime}
                        onEndTimeChange={setEndTime}
                        locked={props.locked}
                    />
                    {publications.length === 0 && (
                        <div className={styles.empty}>
                            <p>Nothing in the feed is scheduled</p>
                            <Button
                                className="primary"
                                onClick={() => setMode('timeline')}
                            >
                                Go to Timeline View
                            </Button>
                        </div>
                    )}
                </>
            )}

            <aside className={styles.saveMenu}>
                <p>{hasChanges ? 'Unsaved Changes' : 'No Changes'}</p>
                <Button
                    onClick={() => copyToEnvironment()}
                    variant="outlined"
                    disabled={hasChanges || isCopying}
                    loading={isCopying}
                    className={styles.copyButton}
                >
                    {isCopying ? 'Copying Schedule...' : 'Copy Schedule'}
                </Button>
                <ProtectedButton
                    role={UserRoles.PortalWriteSchedule}
                    variant="contained"
                    color="primary"
                    disabled={!hasChanges || props.locked}
                    loading={props.isSaving}
                    onClick={saveChanges}
                >
                    Save
                </ProtectedButton>
            </aside>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                slotProps={{
                    content: {
                        className: styles.errorSnackbar,
                    },
                }}
            />
        </>
    );
};
